import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useTranslation from '../hooks/useTranslation';
import './HeroSlider.css';

const SLIDES_BG = [
  'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=1920&q=80&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1920&q=80&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=1920&q=80&auto=format&fit=crop',
];

const INTERVAL = 5000;

// Framer variants
const bgVariants = {
  enter: { opacity: 0, scale: 1.08 },
  center: { opacity: 1, scale: 1, transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, scale: 1.04, transition: { duration: 0.8, ease: 'easeIn' } },
};

const textVariants = {
  enter: { opacity: 0, y: 36 },
  center: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.3 } },
  exit: { opacity: 0, y: -24, transition: { duration: 0.4, ease: 'easeIn' } },
};

const HeroSlider = () => {
  const { t, isRTL } = useTranslation();
  const slides = t('hero.slides');
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, INTERVAL);
    return () => clearInterval(id);
  }, [next, paused]);

  const goTo = (i) => {
    setCurrent(i);
    setPaused(true);
    setTimeout(() => setPaused(false), 8000);
  };

  const slide = Array.isArray(slides) ? slides[current] : { title: '', subtitle: '', cta: '' };
  const ctaHref = current === 1 ? '#services' : '#contact';

  const handleCTA = (e) => {
    e.preventDefault();
    const el = document.querySelector(ctaHref);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="hero"
      aria-label={isRTL ? 'الصفحة الرئيسية' : 'Hero section'}
    >
      {/* Background slides */}
      <AnimatePresence mode="sync">
        <motion.div
          key={`bg-${current}`}
          className="hero__bg"
          style={{ backgroundImage: `url(${SLIDES_BG[current]})` }}
          variants={bgVariants}
          initial="enter"
          animate="center"
          exit="exit"
          aria-hidden="true"
        />
      </AnimatePresence>

      {/* Overlay */}
      <div className="hero__overlay" aria-hidden="true" />

      {/* Content */}
      <div className="hero__content container">
        <AnimatePresence mode="wait">
          <motion.div
            key={`text-${current}`}
            className="hero__text"
            variants={textVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <h1 className="hero__title">{slide.title}</h1>
            <p className="hero__subtitle">{slide.subtitle}</p>
            <motion.a
              href={ctaHref}
              className="btn-accent hero__cta"
              onClick={handleCTA}
              whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(200,168,107,0.55)' }}
              whileTap={{ scale: 0.97 }}
            >
              {slide.cta}
              <span className="hero__cta-arrow" aria-hidden="true">
                {isRTL ? '←' : '→'}
              </span>
            </motion.a>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dots */}
      <div className="hero__dots" role="tablist" aria-label={isRTL ? 'شرائح العرض' : 'Slide navigation'}>
        {Array.isArray(slides) && slides.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === current}
            aria-label={`${isRTL ? 'الشريحة' : 'Slide'} ${i + 1}`}
            className={`hero__dot${i === current ? ' hero__dot--active' : ''}`}
            onClick={() => goTo(i)}
            id={`hero-dot-${i}`}
          />
        ))}
      </div>

      {/* Scroll hint */}
      <motion.div
        className="hero__scroll-hint"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        aria-hidden="true"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 5v14M5 12l7 7 7-7" />
        </svg>
      </motion.div>
    </section>
  );
};

export default HeroSlider;
